import { AppDispatch } from '../..';
import IntlStorage from '../../../infrastructure/intl/IntlStorage';
import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import CachedSchemaRepository from '../../../infrastructure/schemas/storage/CachedSchemaRepository';
import CommandService from '../../../services/commands/CommandService';
import { loadSchemaIconFont } from '../../../ui/utils/loadSchemaIconFont';
import { AppState } from '../../reducers/root';
import { getActiveCategoriesNames } from '../../selectors/category';
import { getAllDatasetsIds } from '../../selectors/dataset';
import { getAuthenticated } from '../../selectors/session';
import commands from '../../slices/commands';
import dataset from '../../slices/dataset';
import schema from '../../slices/schema';
import session, { AuthResult } from '../../slices/session';
import {
  apiCall,
  createAppListener,
  onceInit,
  postDispatchAny,
} from '../listenerHelpers';

const schemaManagementListener = createAppListener();

const apiProvider = new MerpsApiProvider();
const schemaRepository = new CachedSchemaRepository();
const intlStorage = new IntlStorage();
const commandService = new CommandService();

const purgeSchemaCache = async (): Promise<void> => {
  await schemaRepository.purgeCache();
  await intlStorage.purgeCache();
  await commandService.purgeCache();
};

const updateActiveCategoriesSchemas = async (
  state: AppState,
  dispatch: AppDispatch
) => {
  for (const name of getActiveCategoriesNames(state)) {
    await schemaRepository.getCategory(name);
  }

  for (const datasetId of getAllDatasetsIds(state)) {
    dispatch(dataset.updateRecordViewMap({ datasetId }));
    dispatch(commands.get({ datasetId }));
  }
};

schemaManagementListener.startListening(
  onceInit((state, dispatch) => {
    apiProvider.schemas.addListenerOnSchemasUpdated(async () => {
      await updateActiveCategoriesSchemas(state, dispatch);
      await loadSchemaIconFont().catch(() => undefined);
    });

    apiProvider.schemas.addListenerOnSchemasUpdate(async () => {
      await purgeSchemaCache();
    });
  })
);

schemaManagementListener.startListening(
  postDispatchAny(
    [
      session.signInSuccess,
      session.bindStateToExistingSession,
      session.reconnectionSuccess,
      schema.purgeCacheSuccess,
    ],
    (_payload, state) => {
      if (getAuthenticated(state) === AuthResult.YES) {
        void loadSchemaIconFont().catch(() => undefined);
      }
    }
  )
);

schemaManagementListener.startListening(
  apiCall(
    {
      request: schema.purgeCache,
      success: schema.purgeCacheSuccess,
      failure: schema.purgeCacheFailure,
    },
    async (_payload, state, dispatch) => {
      await purgeSchemaCache();
      await updateActiveCategoriesSchemas(state, dispatch);
    }
  )
);

export default schemaManagementListener.middleware;
